import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Highcharts from 'highcharts';
import { COMPANIES_ID } from 'projects/common/src/app/enumerables/companies-id';
import { CacheService } from 'projects/common/src/app/services/cache.service';
import { ParameterHandlerService } from 'projects/common/src/app/services/parameter-handler.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { firstLetterUpper } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { CommunicationService } from 'src/app/services/communication';
import { ProductionByMachineComponent } from '../production-by-machine/production-by-machine.component';
import { ProductionByMachineService } from '../production-by-machine/production-by-machine.service';
import { DatePresentationPipe } from '../../../../core/pipes/date-presentation.pipe';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';

@Component({
  selector: 'app-transfer-scoop',
  templateUrl: './transfer-scoop.component.html',
  styleUrls: ['./transfer-scoop.component.scss'],
})
export class TransferScoopComponent extends ProductionByMachineComponent implements OnInit {
  public dataTransferScoop: any;
  public hasData: boolean;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected langService: LanguageService,
    protected modalService: NgbModal,
    protected productionService: ProductionByMachineService,
    protected formatDatePipe: InputDateFormatPipe,
    protected datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(
      progressBarService,
      communication,
      parameterService,
      sidebarService,
      cacheService,
      machineContext,
      langService,
      modalService,
      productionService,
      formatDatePipe,
      datePipe,
      screenViewService,
    );
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasDate: true,
      hasShift: true,
      hasCompany: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  ngOnInit() {
    super.ngOnInit();
  }

  /**
   * Function to open front detail modal and load data
   */
  public async drawView(chartOptions: any): Promise<void> {
    this.hasData = !!chartOptions.length;
    if (chartOptions.length) {
      this.dataTransferScoop = chartOptions;

      const machines = this.dataTransferScoop.map((m) => m.machineName);
      const serie = this.dataTransferScoop.map((m) => m.distance);
      const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
      // const machineTypeName = this.langService.message(machineType.name);
      const date = this.formatDatePipe.transform(this.parameterService.parameters.item('date').value);
      const shift = this.parameterService.parameters.item('shift').value as IShift;
      // const shiftLang = this.langService.message('shift_');
      const companyId = this.cacheService.local('session').user.company.id;
      const isHcm = companyId === COMPANIES_ID.HCM;
      const user = this.parameterService.parameters.item('user-data').value;
      const xAxisLabel = this.langService.message('equipments').toUpperCase();

      const unit = firstLetterUpper(this.langService.message('meters'));
      const yAxisLabel = this.langService.message('distance').toUpperCase();

      const subtitleChart = `${this.langService.message('company')}: ${user.company.name},
      ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
      ${this.langService.message('date')}: ${this.datePipe.transform(date)},
      ${this.langService.message('shift_')}: ${shift.name}`; // `Empresa: ${user.company.name}, Equipo: ${machineTypeName}, Fecha: ${date}, ${shiftLang} : ${shift.name}`;

      let shiftTooltip = '';

      if (shift.name === this.langService.message('day_detail')) {
        shiftTooltip = this.langService.message('empty');
      } else {
        shiftTooltip = this.langService.message('shift_').toUpperCase();
      }

      const paramsChart = {
        unit,
        serie,
        subtitleChart,
        shift,
        date,
        xAxisLabel,
        shiftTooltip,
        machines,
        yAxisLabel,
        isHcm,
      };

      this.serieConfigChart(paramsChart);
    }
  }

  /**
   * Function to open front detail modal and load data
   */
  protected serieConfigChart(params: any): void {
    const barSerie = {
      dataLabels: {
        enabled: true,
        style: {
          color: '#707070',
          fontSize: '11px',
          fontWeight: 600,
        },
        crop: false,
        overflow: 'none',
      },
      meditionUnit: '',
      name: params.unit,
      type: 'column',
      data: params.serie,
      color: 'rgba(34, 217, 131, 0.5)',
      tooltip: {
        valueSuffix: params.unit,
      },
      states: {
        hover: {
          color: 'rgba(34, 217, 131, 1)',
        },
      },
      events: {
        legendItemClick(e): void {
          e.preventDefault();
        },
      },
      showInLegend: true,
    };
    const series = [barSerie];
    const data = {
      chart: {
        type: 'column',
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: '#F8F8F8',
        style: {
          fontFamily: 'Open Sans',
        },
      },
      title: {
        text: params.chartTitle,
        style: {
          color: '#433e3e',
          fontWeight: '600',
          fontSize: '12px',
        },
      },
      subtitle: {
        text: params.isHcm ? params.subtitleChart : '',
      },
      credits: {
        enabled: false,
      },
      plotOptions: {
        series: {
          borderWidth: 2,
          borderColor: '#22D983',
        },
      },
      tooltip: {
        backgroundColor: 'rgb(51, 51, 51, 1)',
        borderColor: '#333333',
        borderRadius: 2,
        style: {
          color: '#FFFFFF',
        },
        useHTML: true,
        formatter(): string {
          let divSymbol = '';
          divSymbol = `<div style="background-color: #22d983;
                                  width: 30px;
                                  height: 12px;
                                  border: 1px solid #22d983;
                                  float: left;
                                  margin-top: 2px">
                              </div>`;
          return `<div style="width: 180px;
                                      font-size: 10px;
                                      color: #FFFFFF;">
                            <strong style="font-weight: 700;"> ${params.shiftTooltip}
                            ${params.shift && params.shift.name.toUpperCase()}</strong> <br>
                            <span style="font-weight: 400;">${this.x}</span> <br>
                            ${divSymbol} &nbsp;
                            <span style="font-weight: 400;">${this.y} ${params.unit}</span>
                          </div>`;
        },
      },
      legend: {
        layout: 'horizontal',
        squareSymbol: false,
        align: 'center',
        verticalAlign: 'top',
        margin: 30,
        useHTML: true,
        labelFormatter(): string {
          return `<div style="height: 20px; display: flex;">
                              <svg width="36" height="18" style="float: left;">
                                <rect width="30px" height="12px" x="2" y="2"
                                style="fill: ${this.color}; stroke-width: 2px !important;
                                stroke: #22d983 !important;" />
                              </svg>
                              <span style="font-size: 12px; font-family: Open Sans; font-weight: 400;
                              color: #433E3E; float: left; margin-left: 7px">${this.name}</span>
                            </div>`;
        },
        symbolPadding: 0.001,
        symbolWidth: 0.001,
        symbolRadius: 0.001,
        itemStyle: {
          cursor: 'default',
        },
      },
      xAxis: {
        lineWidth: 1,
        lineColor: '#707070',
        title: {
          text: params.xAxisLabel,
          style: {
            fontSize: '12px',
            color: '#707070',
            fontWeight: '600',
          },
        },
        categories: params.machines,
        gridLineWidth: 1,
        labels: {
          useHTML: true,
          style: {
            fontSize: '10px',
            color: '#707070',
            fontWeight: '400',
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      yAxis: [
        {
          title: {
            text: params.yAxisLabel,
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
          labels: {
            style: {
              fontSize: '10px',
              color: '#707070',
              fontWeight: '400',
            },
          },
          lineWidth: 1,
          lineColor: '#707070',
        },
        {
          opposite: true,
          lineWidth: 1,
          min: 0,
          lineColor: '#707070',
          title: {
            text: '',
          },
        },
      ],
      series,
    };
    this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
      if (!this.hasData) {
        const newX = chart.plotWidth / 3 + chart.plotLeft;
        const newY = chart.plotHeight / 2 + chart.plotTop;
        chart.renderer
          .text('La solicitud no arrojó resultados', newX, newY)
          .css({
            color: '#4572A7',
            fontSize: '24px',
          })
          .add();
      }
    });
  }
}
